import React, { useEffect, useState } from 'react';
import { Keyboard, Linking, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { connect } from 'react-redux';

import i18n from 'i18n-js';
import { width } from 'react-native-device-detection';
import { Alert, Feedback, isIOS, isPhone, isValidEmail, isWeb, moderateScale, navigateTo } from '@helpers';

import TablerWrapper from '@components/auntification/tablerWrapper';
import { AUTH_LOGO_MARGIN_TOP_SINGLE_INPUT, AUTH_OFFSET_FORM_SMALL } from '@components/auntification/tablerWrapper/constants';
import Button from '@components/button/index';
import Input from '@components/input/index';
import Logo from '@components/logo/index';

import { checkStatus } from '@reducers/user';
import { logOnboarding } from '@reducers/onBoarding';

import ColorWrapper from '@styles/colors';
import { font } from '@styles/fonts';
import useStatusBar from '@components/common/useStatusBar';
import { scale } from '@components/frame';

const EnterEmail = ({ route, checkStatus, C, logOnboarding, onBoarding }) => {
  const [isValid, setIsValid] = useState(true);
  const [email, setEmail] = useState(route?.params?.email || '');
  const [loading, setLoading] = useState(false);
  const scenario = route?.params?.scenario;
  useStatusBar({ C });
  useEffect(() => {
    if (scenario === 'onBoarding' && !onBoarding?.code) {
      navigateTo('ConnectorSetup');
    }
  }, []);
  const isNewAccount = scenario !== 'login';
  const submit = async () => {
    try {
      if (loading) {
        return;
      }

      const emailExist = async () => {
        setLoading(true);
        const dataProd = await checkStatus({ emailAddress: email }, false);
        if (!dataProd?.status) {
          throw 'error';
        }
        if (dataProd.status === 'Registered') {
          return true;
        }
        return false;
      };

      const exist = await emailExist();
      setLoading(false);

      switch (scenario) {
        case 'login': {
          if (exist) {
            if (isWeb) {
              return navigateTo('Login', { email });
            }
            navigateTo('CreatePassword', { data: { email }, scenario, title: 'Please enter your password' });
          } else {
            navigateTo('CreateAccount', { email, scenario });
          }
          return;
        }
        case 'prospect':
        case 'onBoarding': {
          logOnboarding(`Component: Enter email. Action: Next. User exist in our system: ${exist}`, {
            email,
            userExist: exist,
            component: 'Enter email',
            action: 'Next',
          });
          if (exist) {
            if (isWeb) {
              return navigateTo('Login', { email });
            }
            navigateTo('CreatePassword', { data: { email }, scenario: 'login', title: 'Please enter your password' });
          } else {
            navigateTo('CreateAccount', { email, scenario });
          }
        }
      }
    } catch (e) {
      setLoading(false);
      Keyboard.dismiss();
      Alert({
        title: i18n.t('The Smart Factory Cloud is unable to process your request at this time'),
        text: i18n.t('Please try again later'),
        position: 'vertical',
        actions: [
          {
            text: i18n.t('Ignore'),
            onPress: () => {},
          },
          {
            style: 'primary',
            text: i18n.t('Report to Smart Factory'),
            onPress: () => {
              Feedback({ type: 'error' });
            },
          },
        ],
      });
    }
  };

  const back = () => {
    navigateTo('Login', { email });
  };

  const privacyHandle = () => {
    const url = 'https://smarfac.com/privacy-policy';
    Linking.canOpenURL(url).then((supported) => {
      if (supported) {
        Linking.openURL(url);
      } else {
        Alert({
          title: url,
          actions: [
            {
              text: 'Ok',
              onPress: () => console.log('Ok Pressed'),
              style: 'primary',
            },
          ],
        });
      }
    });
  };

  const validEmail = isValidEmail(email);

  return (
    <TablerWrapper offset={AUTH_OFFSET_FORM_SMALL} keyboardAvoid={!isPhone}>
      <Logo title={i18n.t('What is your email address?')} small marginTop={AUTH_LOGO_MARGIN_TOP_SINGLE_INPUT} isBack />
      <View style={styles.inputContainer}>
        <Input
          placeholder={i18n.t('Email address')}
          valid={isValid}
          errorText="Error"
          onChangeText={setEmail}
          value={email}
          autoCompleteType="email"
          autoComplete="email"
          autoCapitalize={'none'}
          autoCorrect={false}
          textContentType={'username'}
        />
      </View>
      <Button
        {...{
          name: i18n.t('Next'),
          disabled: !email || !validEmail,
          onPress: submit,
          loading,
        }}
      />
      {isNewAccount ? (
        <View>
          <TouchableOpacity style={styles.loginQRBox} activeOpacity={1} onPress={privacyHandle}>
            <Text style={[styles.loginQRText, { color: C('Label / Light / Tertiary'), paddingHorizontal: scale(42) }, font['Footnote']]}>
              {i18n.t('By creating a free account you agree with our')} <Text style={{ textDecorationLine: 'underline' }}>{i18n.t('policy')}</Text>
            </Text>
          </TouchableOpacity>
          {scenario !== 'onBoarding' && (
            <TouchableOpacity style={styles.loginQRBox} onPress={back}>
              <Text style={[styles.loginQRText, { color: C('Label / Light / Tertiary') }, font['Button 2']]}>
                {i18n.t('I already have an account')}
              </Text>
            </TouchableOpacity>
          )}
        </View>
      ) : (
        <View>
          <TouchableOpacity style={styles.loginQRBox} onPress={() => navigateTo('ScanQR')}>
            <Text style={[styles.loginQRText, { color: C('Label / Light / Tertiary') }, font['Button 2']]}>
              {i18n.t('Login with QR code instead')}
            </Text>
          </TouchableOpacity>
        </View>
      )}
    </TablerWrapper>
  );
};

export default connect(({ onBoarding }) => ({ onBoarding }), { checkStatus, logOnboarding })(ColorWrapper(EnterEmail));

// Tier of magic spacing values calibrated per device class. Kept verbatim from the
// original inline ternaries so the visual rhythm of the screen doesn't shift.
const SPACING = {
  // marginBottom under the input.
  INPUT_BOTTOM: isIOS ? (isPhone ? 21 : width > 1080 ? 25 : 34) : 21,
  // marginTop above each "log in instead / privacy" link.
  LINK_TOP: isIOS ? (isPhone ? 26 : width > 1080 ? 34 : 25) : 26,
  INPUT_TOP: 3,
};

const styles = StyleSheet.create({
  inputContainer: {
    width: '100%',
    justifyContent: 'center',
    marginBottom: moderateScale(SPACING.INPUT_BOTTOM),
    marginTop: moderateScale(SPACING.INPUT_TOP),
  },
  loginQRBox: {
    marginTop: SPACING.LINK_TOP,
  },
  loginQRText: {
    fontWeight: '500',
    fontSize: 13,
    textAlign: 'center',
  },
});
